import { format } from "date-fns";
import {
  useGetIdentity,
  useGetList,
  useNotify,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useEffect, useRef, useState } from "react";

import type { PersonalTodo } from "../types";
import { showSystemNotification } from "./notifications";
import { parseDateOnly } from "./todoSchedule";
import { useDateLocale } from "./useDateLocale";

const POLL_INTERVAL_MS = 60_000;

/**
 * Fires the to-do reminders that are due: an in-app toast plus a system
 * notification when allowed. Each reminder fires once (`reminded_at` is set).
 * Runs while the CRM is open, from the app layout.
 */
export const usePersonalReminders = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { dateFnsLocale } = useDateLocale();
  const { identity } = useGetIdentity();
  const [update] = useUpdate();
  const [now, setNow] = useState(() => new Date().toISOString());
  const firedIds = useRef(new Set<Identifier>());

  useEffect(() => {
    const interval = setInterval(
      () => setNow(new Date().toISOString()),
      POLL_INTERVAL_MS,
    );
    return () => clearInterval(interval);
  }, []);

  const { data: dueTodos } = useGetList<PersonalTodo>(
    "personal_todos",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "remind_at", order: "ASC" },
      filter: {
        sales_id: identity?.id,
        "done_at@is": null,
        "reminded_at@is": null,
        "remind_at@lte": now,
      },
    },
    { enabled: identity?.id != null },
  );

  useEffect(() => {
    dueTodos
      ?.filter((todo) => !firedIds.current.has(todo.id))
      .forEach((todo) => {
        firedIds.current.add(todo.id);
        const title = translate("crm.personal.reminders.notification_title", {
          title: todo.title,
        });
        const body = translate("crm.personal.reminders.due", {
          date: format(parseDateOnly(todo.due_date), "PPP", {
            locale: dateFnsLocale,
          }),
        });
        notify(`${title} — ${body}`, {
          type: "info",
          autoHideDuration: 10_000,
        });
        showSystemNotification(title, {
          body: todo.description ? `${body}\n${todo.description}` : body,
          tag: `personal-todo-${todo.id}`,
        });
        update("personal_todos", {
          id: todo.id,
          data: { reminded_at: new Date().toISOString() },
          previousData: todo,
        });
      });
  }, [dueTodos, translate, notify, update, dateFnsLocale]);
};

/** Renderless component so layouts can mount the reminders in one line. */
export const PersonalReminders = () => {
  usePersonalReminders();
  return null;
};
